
import React, { useState, useMemo, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import { useToast } from './contexts/ToastContext';
import { Page, Store } from './types.ts';
import { canAccessPage } from './config/pageAccess';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider, useAuth, type Tenant } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { SystemHealthProvider } from './contexts/SystemHealthContext';
import { UserProvider } from './contexts/UserContext';
import { CalmModeProvider } from './contexts/CalmModeContext';
import ToastContainer from './components/common/Toast';
import SkeletonLogin from './components/auth/SkeletonLogin';
import { api } from './services/api';
import { supabase } from '@/services/supabase';
import { useRealtimeAlerts } from '@/features/security/hooks/useRealtimeAlerts';
import { useViewTransition } from '@/hooks/useViewTransition';
import { useINPMonitor } from '@/hooks/usePerformanceMetrics';
import { useStoreStatuses } from '@/features/inventory/hooks/useDashboardData';
import { useAppRouter } from '@/hooks/useAppRouter';
import { DrilldownProvider } from '@/contexts/DrilldownContext';
import { GlobalFilterProvider } from '@/contexts/GlobalFilterContext';
import { ForensicAccessProvider } from '@/hooks/useForensicAccess';
import { ForensicBanner } from '@/components/common/ForensicToggle';
import { Lock, AlertTriangle, RefreshCw } from 'lucide-react';
import ErrorBoundary from '@/components/common/ErrorBoundary';

// Retry dynamic imports once then reload on chunk load failure (stale deployments)
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((err) => {
      const key = 'chunk_reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
      throw err;
    })
  );
}

// Lazy load heavy authenticated components (code splitting)
const MultiStoreDashboard = lazyWithRetry(() => import('./components/multistore/MultiStoreDashboard'));
const SupervisorDashboard = lazyWithRetry(() => import('./components/multistore/SupervisorDashboard'));
const AlertsQueue = lazyWithRetry(() => import('@/features/security/components/AlertsQueue'));
const CaseManagement = lazyWithRetry(() => import('@/features/security/components/CaseManagement'));
const ReportsDashboard = lazyWithRetry(() => import('./components/reports/ReportsDashboard'));
const SettingsPage = lazyWithRetry(() => import('./components/admin/SettingsPage'));
const TaskBoard = lazyWithRetry(() => import('@/features/compliance/components/TaskBoard'));
const AuditorDashboard = lazyWithRetry(() => import('@/features/inventory/components/AuditorDashboard'));
const CashierPerformanceDashboard = lazyWithRetry(() => import('@/features/security/components/CashierPerformanceDashboard'));
const ResponsiveLayout = lazyWithRetry(() => import('./components/layout/ResponsiveLayout'));

// Lazy load public components (separate chunk)
const PublicHome = lazyWithRetry(() => import('./components/auth/PublicHome'));
const AuthGateway = lazyWithRetry(() => import('./components/auth/AuthGateway'));
const RegisterFlow = lazyWithRetry(() => import('./components/auth/RegisterFlow'));
const PricingCalculator = lazyWithRetry(() => import('./components/auth/PricingCalculator'));

// Lazy load onboarding (separate chunk)
const OnboardingWizard = lazyWithRetry(() => import('./components/onboarding/OnboardingWizard'));
const EntitySetupWizard = lazyWithRetry(() => import('./components/onboarding/EntitySetupWizard'));

// Lazy load smart landing for decision intelligence (v4.0.0)
const SmartLanding = lazyWithRetry(() => import('./components/common/SmartLanding'));

// Lazy load layer pages (v2.1.0 - drill-down architecture)
const ExecutivePage = lazyWithRetry(() => import('./features/layers/pages/ExecutivePage'));
const OperationsPage = lazyWithRetry(() => import('./features/layers/pages/OperationsPage'));
const DistrictPage = lazyWithRetry(() => import('./features/layers/pages/DistrictPage'));
const StorePage = lazyWithRetry(() => import('./features/layers/pages/StorePage'));
const FloorPage = lazyWithRetry(() => import('./features/layers/pages/FloorPage'));

// Lazy load Command Center (v4.6.0 - unified role-adaptive view)
const CommandCenterPage = lazyWithRetry(() => import('./features/command-center/pages/CommandCenterPage'));

// Lazy load Forensics (v5.2.0 - network forensic & collusion)
const ForensicsPage = lazyWithRetry(() => import('./features/forensics/pages/ForensicsPage'));

// Lazy load Supply Chain (v5.34.0 - supplier agreements & fulfillment)
const SupplyChainPage = lazyWithRetry(() => import('./features/supply-chain/pages/SupplyChainPage'));

// Lazy load VSM Explorer (v5.99.0 - value stream map for retail LP)
const VSMExplorerPage = lazyWithRetry(() => import('./features/vsm/pages/VSMExplorerPage'));
const ExpedientePage = lazyWithRetry(() => import('./features/expediente/pages/ExpedientePage'));
const LiveOpsPage = lazyWithRetry(() => import('./features/live-ops/pages/LiveOpsPage'));

// Lazy load MVP Dashboard (v6.9.0 — single-page detection-loop tablero)
const MvpDashboardPage = lazyWithRetry(() => import('./features/mvp/pages/MvpDashboardPage'));

// Lazy load Risk Control Tower prototype (v6.9.0 — design-system showcase, Executive → Store → Alert)
const RiskTowerPage = lazyWithRetry(() => import('./features/risk-tower/pages/RiskTowerPage'));

// Lazy load Inventory Overview (v6.13.0 — 9-tile lifecycle matrix)
const InventoryOverviewPage = lazyWithRetry(() => import('./features/inventory-overview/pages/InventoryOverviewPage'));
const OperationsTabPage = lazyWithRetry(() => import('./features/operations/pages/OperationsPage'));
const GlossaryAdminPage = lazyWithRetry(() => import('./components/admin/GlossaryAdmin'));
const KpiBuilderPage = lazyWithRetry(() => import('./components/kpi-builder/KpiBuilderPage'));
const DiagnosticoPage = lazyWithRetry(() => import('./features/diagnostico/DiagnosticoPage'));
const RuleDesignerPage = lazyWithRetry(() => import('./components/kpi-rules/RuleDesigner'));
const RiskTowerPackPage = lazyWithRetry(() => import('./features/risk-tower/pages/RiskTowerPackPage'));

// Lazy load System Admin Console (separate chunk for /sysadmin routes)
const SysAdminEntry = lazyWithRetry(() => import('./components/sysadmin/SysAdminEntry'));

const AuthenticatedApp: React.FC = () => {
    const { tenant, signOut } = useAuth();
    const queryClient = useQueryClient();
    const { addToast } = useToast();
    // Fetch stores for inline store selector (when no store selected in Control Tower)
    const { data: storeStatuses, isLoading: storesLoading } = useStoreStatuses(tenant?.id);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Subscribe to real-time alert events for toast notifications and cache invalidation
    // SECURITY: Pass tenantId for tenant-scoped subscription
    useRealtimeAlerts(tenant?.id);

    // Subscribe to real-time store INSERT events so new stores appear without full page reload
    // SECURITY: tenant_id filter ensures tenant isolation on the subscription
    useEffect(() => {
        if (!tenant?.id) return;
        const channel = supabase.channel('store-changes')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'stores',
                filter: `tenant_id=eq.${tenant.id}`,
            }, () => {
                queryClient.invalidateQueries({ queryKey: ['storeStatuses'] });
                queryClient.invalidateQueries({ queryKey: ['store-leaderboard'] });
                queryClient.invalidateQueries({ queryKey: ['tenant-aggregates'] });
            })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [tenant?.id, queryClient]);

    // Performance Monitoring - INP (2026 Web Standards)
    // Logs warnings for interactions slower than 200ms
    useINPMonitor(200);

    // View Transitions API integration (2026 CSS-Native Standard)
    const { startTransition, isSupported: viewTransitionsSupported } = useViewTransition({
        respectReducedMotion: true,
    });

    // URL-based routing
    const { route, navigate, setAdminSection, setSettingsSection, setTab } = useAppRouter();
    const activePage = route.page;

    // Derive selectedStore from route.storeId + loaded storeStatuses
    const selectedStore = useMemo(() => {
        if (!route.storeId || !storeStatuses) return null;
        return storeStatuses.find(s => s.store.id === route.storeId)?.store ?? null;
    }, [route.storeId, storeStatuses]);

    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [userKey, setUserKey] = useState<number>(0); // To force re-render on user switch

    const handleLogout = async () => {
        await signOut();
        navigate('overview');
    };

    // Navigation with View Transitions
    const handleSelectStore = useCallback((store: Store) => {
        startTransition(() => {
            navigate('dashboard', { storeId: store.id });
        });
    }, [startTransition, navigate]);

    const handleNavigate = useCallback((page: Page, storeOrOpts?: Store | { storeId?: string; ruleId?: string; phase?: string }) => {
        startTransition(() => {
            const storeId = storeOrOpts
                ? 'id' in storeOrOpts ? storeOrOpts.id : storeOrOpts.storeId
                : undefined;
            const ruleId = storeOrOpts && !('id' in storeOrOpts) ? storeOrOpts.ruleId : undefined;
            const phase = storeOrOpts && !('id' in storeOrOpts) ? storeOrOpts.phase : undefined;
            navigate(page, { storeId, ruleId, phase });
        });
    }, [startTransition, navigate]);

    const canAccessForRole = useCallback(
        (page: Page) => canAccessPage(tenant?.role || 'store_manager', page),
        [tenant?.role]
    );

    /**
     * Global refresh handler - invalidates ALL query caches across all sections
     * This is called from the Header refresh button and refreshes data for:
     * - Control Tower (KPIs, inventory, financials)
     * - Alerts section
     * - Cases section
     * - Tasks section
     * - Multi-store/Global view
     */
    const handleRefresh = useCallback(async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);

        try {
            // Build array of all invalidation promises
            const invalidationPromises: Promise<void>[] = [
                // ========== CONTROL TOWER / STORE-LEVEL ==========
                // Invalidate without storeId to refresh ALL stores' data
                queryClient.invalidateQueries({ queryKey: ['kpis'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['inventoryQuality'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['storeFinancials'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['extendedFinancials'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['operationalCompliance'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['inventoryInsights'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['storeZones'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['processErrors'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['zoneMetrics'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['riskTrend'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['operationalKPIs'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['kpiDetails'], refetchType: 'active' }),

                // ========== ALERTS SECTION ==========
                queryClient.invalidateQueries({ queryKey: ['alerts'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['riskPatterns'], refetchType: 'active' }),

                // ========== CASES SECTION ==========
                queryClient.invalidateQueries({ queryKey: ['cases'], refetchType: 'active' }),

                // ========== TASKS SECTION ==========
                queryClient.invalidateQueries({ queryKey: ['tasks'], refetchType: 'active' }),

                // ========== MULTI-STORE / GLOBAL VIEW ==========
                queryClient.invalidateQueries({ queryKey: ['globalOverview'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['storeStatuses'], refetchType: 'active' }),

                // ========== SUPPLY CHAIN ==========
                queryClient.invalidateQueries({ queryKey: ['pipeline-stock'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['pipeline-summary'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['supplier-agreements'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['supplier-fulfillment'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['agreement-compliance'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['po-stats'], refetchType: 'active' }),
            ];

            // Add tenant-specific queries if tenant is available
            if (tenant?.id) {
                invalidationPromises.push(
                    queryClient.invalidateQueries({ queryKey: ['tenant-aggregates', tenant.id], refetchType: 'active' }),
                    queryClient.invalidateQueries({ queryKey: ['store-leaderboard', tenant.id], refetchType: 'active' })
                );
            }

            await Promise.all(invalidationPromises);
            setRefreshKey(prev => prev + 1);
            addToast('Datos actualizados en todas las secciones', 'success');
        } catch (error) {
            console.error('[App] Failed to refresh data:', error);
            addToast('Error al actualizar datos', 'error');
        } finally {
            setIsRefreshing(false);
        }
    }, [isRefreshing, queryClient, tenant?.id, addToast]);

    const pageTitle = useMemo(() => {
        const userRole = tenant?.role || 'store_manager';
        const baseTitles: Record<Page, string> = {
            overview: userRole === 'store_manager' ? 'Resumen Operativo' : 'Vista Global de Riesgo',
            dashboard: 'Monitor de Tienda',
            alerts: userRole === 'auditor' ? 'Hallazgos' : 'Centro de Alertas',
            cases: 'Investigaciones',
            reports: 'Inteligencia de Negocio',
            admin: 'Ajustes',
            settings: 'Ajustes',
            tasks: 'Operaciones de Campo',
            'auditor-home': 'Panel de Auditoría',
            'cashier-performance': 'Análisis Punto de Venta',
            'command-center': 'Centro de Mando',
            forensics: 'Análisis Forense',
            executive: 'Vista Ejecutiva',
            operations: 'Operaciones',
            district: userRole === 'store_manager' ? 'Mi Zona' : 'Zonas',
            floor: 'Piso',
            'supply-chain': 'Cadena de Abastecimiento',
            vsm: 'Cadena de Valor',
            expediente: 'Expediente',
            'live-ops': 'Operaciones en Vivo',
            mvp: 'Tablero MVP',
            'risk-tower': 'Torre de Riesgo',
            'inventory-overview': 'Vista General de Inventario',
            'tablero-operativo': 'Tablero Operativo',
            'glossary': 'Diccionario',
            'constructor-kpis': 'Constructor de KPIs',
            'constructor-reglas': 'Constructor de Reglas',
            'risk-tower-pack': 'Torre de Riesgo · Pack',
            'diagnostico': 'Diagnóstico de Inventario',
        };

        if (activePage === 'dashboard' && selectedStore) {
            return `Monitor: ${selectedStore.name}`;
        }
        if (activePage === 'floor' && selectedStore) {
            return `Piso: ${selectedStore.name}`;
        }
        return baseTitles[activePage];
    }, [activePage, selectedStore?.name, tenant?.role]);

    // Breadcrumb navigation for drill-down context
    const breadcrumbs = useMemo(() => {
        // Layer pages use their own LayerBreadcrumb component
        if (['executive', 'operations', 'district', 'floor', 'command-center', 'forensics', 'supply-chain', 'vsm', 'live-ops', 'mvp', 'risk-tower'].includes(activePage)) {
            return undefined;
        }
        // Show breadcrumb when drilling down from Global/Executive to Store Control Tower
        if (activePage === 'dashboard' && route.storeId) {
            // Only link to the executive view for roles that can open it
            const canOpenExecutive = canAccessPage(tenant?.role || 'store_manager', 'executive');
            return [
                { label: 'Vista Ejecutiva', page: canOpenExecutive ? ('executive' as Page) : undefined },
                { label: selectedStore?.name ?? 'Tienda' }
            ];
        }
        return undefined;
    }, [activePage, route.storeId, selectedStore?.name, tenant?.role]);


    const renderContent = () => {
        const userRole = tenant?.role || 'store_manager';

        // Auditor restrictions: No access to overview, admin, reports, executive, operations, district
        if (userRole === 'auditor' && !canAccessPage(userRole, activePage)) {
             return (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                    <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        <Lock className="h-8 w-8 text-gray-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-700 mb-2">Acceso Restringido</h2>
                    <p className="text-gray-600 mb-6 max-w-md">No tienes autorización para acceder a esta sección. Por favor, navega a una de las páginas permitidas.</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <button
                            onClick={() => navigate('auditor-home')}
                            className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                        >
                            Mi Ruta
                        </button>
                        <button
                            onClick={() => navigate('dashboard')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                        >
                            Monitor
                        </button>
                        <button
                            onClick={() => navigate('alerts')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                        >
                            Alertas
                        </button>
                        <button
                            onClick={() => navigate('tasks')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                        >
                            Tareas
                        </button>
                    </div>
                </div>
             );
        }

        // Store Manager restrictions: No access to admin, reports, cashier-performance, executive, operations
        // NOTE: Store managers CAN access 'overview', 'district', 'dashboard', 'floor'
        if (userRole === 'store_manager' && !canAccessPage(userRole, activePage)) {
             return (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                    <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        <Lock className="h-8 w-8 text-gray-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-700 mb-2">Acceso Restringido</h2>
                    <p className="text-gray-600 mb-6 max-w-md">Esta sección está reservada para administradores. Puedes gestionar tu tienda desde las páginas permitidas.</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <button
                            onClick={() => navigate('overview')}
                            className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                        >
                            Mi Panel
                        </button>
                        <button
                            onClick={() => navigate('alerts')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                        >
                            Alertas
                        </button>
                        <button
                            onClick={() => navigate('tasks')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                        >
                            Tareas
                        </button>
                        <button
                            onClick={() => navigate('cases')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                        >
                            Casos
                        </button>
                    </div>
                </div>
             );
        }

        switch (activePage) {
            case 'overview':
                // Store managers see SupervisorDashboard (filtered to their assigned stores)
                // Admins/Regional managers see full MultiStoreDashboard
                return userRole === 'store_manager' ? (
                    <SupervisorDashboard onSelectStore={handleSelectStore} />
                ) : (
                    <MultiStoreDashboard
                        onSelectStore={handleSelectStore}
                        onNavigateToAdmin={() => handleNavigate('admin')}
                        onNavigate={(page) => handleNavigate(page as Page)}
                    />
                );
            case 'dashboard':
                return route.storeId ? (
                    <StorePage
                        storeId={route.storeId}
                        storeName={selectedStore?.name}
                        onNavigate={handleNavigate}
                        onBackToCompany={() => {
                            navigate('overview');
                        }}
                    />
                ) : (
                    /* Smart Landing - Decision Intelligence UX (v4.0.0)
                     * Replaces static "Select a Store" with proactive attention items
                     * and quick navigation based on 2026 anticipatory design trends */
                    <SmartLanding
                        onNavigate={handleNavigate}
                        onSelectStore={(storeId) => {
                            const store = storeStatuses?.find(s => s.store.id === storeId)?.store;
                            if (store) handleSelectStore(store);
                        }}
                        availableStores={storeStatuses?.map(s => ({ id: s.store.id, name: s.store.name }))}
                    />
                );
            case 'alerts':
                return <AlertsQueue onNavigate={(page, opts) => handleNavigate(page as Page, opts)} />;
            case 'cases':
                return <CaseManagement />;
            case 'reports':
                return <ReportsDashboard />;
            case 'admin':
            case 'settings': {
                // /admin/:section deep links map onto the Settings sections (all but 'plan' exist there)
                const section = route.page === 'admin' && route.adminSection && route.adminSection !== 'plan'
                    ? route.adminSection
                    : route.settingsSection;
                return <SettingsPage section={section} onSectionChange={setSettingsSection} />;
            }
            case 'tasks':
                return <TaskBoard />;
            case 'auditor-home':
                return <AuditorDashboard onNavigate={handleNavigate} />;
            case 'cashier-performance':
                return <CashierPerformanceDashboard />;
            case 'command-center':
                return <CommandCenterPage onNavigate={handleNavigate} initialTab={route.tab} onTabChange={setTab} />;
            case 'forensics':
                return <ForensicsPage onNavigate={handleNavigate} />;
            case 'supply-chain':
                return <SupplyChainPage onNavigate={handleNavigate} />;
            case 'vsm':
                return <VSMExplorerPage onNavigate={handleNavigate} />;
            case 'expediente':
                return <ExpedientePage />;
            case 'live-ops':
                return route.ruleId ? (
                    <LiveOpsPage ruleId={route.ruleId} onNavigate={handleNavigate} />
                ) : (
                    <div className="flex flex-col items-center justify-center p-12 text-center">
                        <p className="text-gray-600">Seleccione una regla para ver su feed en vivo.</p>
                        <button
                            onClick={() => navigate('mvp')}
                            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                        >
                            Ir al Tablero MVP
                        </button>
                    </div>
                );
            case 'mvp':
                return <MvpDashboardPage onNavigate={handleNavigate} />;
            case 'risk-tower':
                return <RiskTowerPage onNavigate={(page, opts) => handleNavigate(page as Page, opts)} />;
            case 'inventory-overview':
                return <InventoryOverviewPage onNavigate={handleNavigate} />;
            case 'tablero-operativo':
                return <OperationsTabPage onNavigate={handleNavigate} initialPhase={route.phase} />;
            case 'glossary':
                return <GlossaryAdminPage />;
            case 'constructor-kpis':
                return <KpiBuilderPage />;
            case 'constructor-reglas':
                return <RuleDesignerPage />;
            case 'risk-tower-pack':
                return <RiskTowerPackPage onNavigate={handleNavigate} />;
            case 'diagnostico':
                return <DiagnosticoPage />;
            case 'executive':
                return <ExecutivePage onNavigate={handleNavigate} />;
            case 'operations':
                return <OperationsPage onNavigate={handleNavigate} />;
            case 'district':
                return <DistrictPage onNavigate={handleNavigate} />;
            case 'floor':
                return route.storeId ? (
                    <FloorPage
                        storeId={route.storeId}
                        storeName={selectedStore?.name}
                        onNavigate={handleNavigate}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center p-12 text-center">
                        <p className="text-gray-600">Seleccione una tienda para ver la vista de piso.</p>
                        <button
                            onClick={() => navigate('dashboard')}
                            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                        >
                            Ir a Torre de Control
                        </button>
                    </div>
                );
            default:
                return <div>Página no encontrada</div>;
        }
    };

    // Content loading fallback for page transitions
    const ContentLoader = () => (
        <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div className="text-gray-900 font-sans selection:bg-primary/30 bg-gradient-warm h-screen w-screen overflow-hidden" key={userKey}>
            <GlobalFilterProvider>
            <ForensicAccessProvider>
            <DrilldownProvider
                activePage={activePage}
                storeId={route.storeId}
                storeName={selectedStore?.name}
                onNavigate={handleNavigate}
                canAccess={canAccessForRole}
            >
                <ForensicBanner />
                <Suspense fallback={<ContentLoader />}>
                    <ResponsiveLayout
                        activePage={activePage}
                        pageTitle={pageTitle}
                        breadcrumbs={breadcrumbs}
                        onNavigate={handleNavigate}
                        onLogout={handleLogout}
                        onRefresh={handleRefresh}
                        refreshKey={refreshKey}
                        selectedStoreId={route.storeId}
                        selectedStoreName={selectedStore?.name}
                    >
                        <ErrorBoundary
                            componentName="AppRoot"
                            fallback={
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center p-8 max-w-md">
                                        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Error inesperado</h2>
                                        <p className="text-sm text-gray-600 mb-6">Ha ocurrido un error al cargar esta página. Intente recargar o volver al inicio.</p>
                                        <div className="flex gap-3 justify-center">
                                            <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">
                                                <RefreshCw className="w-4 h-4" /> Recargar
                                            </button>
                                            <button onClick={() => { window.location.assign('/overview'); }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                                                Ir al Inicio
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            }
                        >
                        <Suspense fallback={<ContentLoader />}>
                            {renderContent()}
                        </Suspense>
                        </ErrorBoundary>
                    </ResponsiveLayout>
                </Suspense>
                <ToastContainer />
            </DrilldownProvider>
            </ForensicAccessProvider>
            </GlobalFilterProvider>
        </div>
    );
};

// Loading fallback for authenticated app
const AuthenticatedAppLoader: React.FC = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-warm text-gray-900">
        <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-lg font-medium">Cargando aplicación...</p>
        </div>
    </div>
);

type PublicPage = 'home' | 'login' | 'register' | 'pricing';

const AppContent: React.FC = () => {
    const { user, tenant, loading, signOut, markOnboardingComplete, handshakeComplete, onboardingStatus, retryProfile } = useAuth();
    const [publicPage, setPublicPage] = useState<PublicPage>(() => {
        // Support /pricing deep link for unauthenticated users
        if (typeof window !== 'undefined' && window.location.pathname === '/pricing') {
            return 'pricing';
        }
        return 'home';
    });

    /**
     * Reset to home page when user logs out.
     * signOut() sets loading=true (shows skeleton), then clears user (shows public pages).
     * Only triggers on user/loading changes — NOT on publicPage changes, to avoid
     * resetting navigation to login/register.
     */
    const prevUserRef = useRef(user);
    useEffect(() => {
        // Only reset when user transitions from authenticated → null (logout)
        if (prevUserRef.current && !user && !loading) {
            setPublicPage('home');
            window.history.replaceState(null, '', '/');
        }
        prevUserRef.current = user;
    }, [user, loading]);

    /**
     * POST-LOGIN HANDSHAKE PROTOCOL - Routing Decision Tree
     *
     * Based on onboardingStatus from AuthContext:
     * - 'founder': User has no tenant → show full onboarding wizard
     * - 'incomplete': User has tenant but not onboarded → show onboarding (pre-fill data)
     * - 'operator': User fully onboarded → BYPASS onboarding, go to dashboard
     * - 'pending'/'error': Still loading or error state
     */
    const shouldShowOnboarding = onboardingStatus === 'founder' || onboardingStatus === 'incomplete';

    // Show loading skeleton while checking auth
    if (loading) {
        return <SkeletonLogin />;
    }

    // Unauthenticated: show public pages (home, login, register)
    // Routing is driven purely by auth state — no isLoginComplete flag needed.
    // signOut() sets loading=true (caught above), so no flash of public content during logout.
    if (!user) {
        return (
            <Suspense fallback={<SkeletonLogin />}>
                {publicPage === 'home' && (
                    <PublicHome
                        onNavigateToLogin={() => setPublicPage('login')}
                        onNavigateToRegister={() => setPublicPage('register')}
                        onNavigateToPricing={() => { setPublicPage('pricing'); window.history.pushState(null, '', '/pricing'); }}
                        onRequestDemo={() => {
                            window.open('mailto:ventas@omnitower.ai?subject=Solicitud%20de%20demostraci%C3%B3n', '_blank');
                        }}
                    />
                )}
                {publicPage === 'login' && (
                    <AuthGateway />
                )}
                {publicPage === 'register' && (
                    <RegisterFlow
                        onNavigateToLogin={() => setPublicPage('login')}
                        onBack={() => setPublicPage('home')}
                    />
                )}
                {publicPage === 'pricing' && (
                    <PricingCalculator
                        onBack={() => { setPublicPage('home'); window.history.pushState(null, '', '/'); }}
                        onRegister={() => { setPublicPage('register'); window.history.pushState(null, '', '/'); }}
                    />
                )}
            </Suspense>
        );
    }

    /**
     * POST-LOGIN HANDSHAKE - Onboarding Flow
     *
     * Route users to onboarding based on their status:
     * - 'founder' (Scenario A): Full onboarding wizard - create organization
     * - 'incomplete' (Scenario B): Onboarding wizard - pre-fill existing data
     * - 'operator' (Scenario C): SKIP this entirely - they bypass onboarding
     */
    if (shouldShowOnboarding && user) {
        console.log('[Router] Routing to onboarding - status:', onboardingStatus);
        return (
            <Suspense fallback={<AuthenticatedAppLoader />}>
                <EntitySetupWizard
                    onComplete={async (_state, _suggestedFlags) => {
                        // Mark user as onboarded - this updates status to 'operator'
                        // Feature flags are already updated by the wizard via TenantContext
                        await markOnboardingComplete();
                    }}
                    onSkip={() => {
                        // Allow skipping onboarding (mark as complete anyway)
                        markOnboardingComplete();
                    }}
                />
            </Suspense>
        );
    }

    /**
     * ERROR RECOVERY: Tenant profile fetch failed
     *
     * When user is authenticated but tenant profile couldn't be loaded
     * (RLS error, expired token, network issue), show a recovery screen
     * instead of falling through to UserProvider which would freeze on
     * SkeletonLogin forever (tenant.id is null → infinite skeleton).
     */
    if (user && !tenant && (onboardingStatus === 'error' || (onboardingStatus === 'pending' && !loading))) {
        console.warn('[Router] Auth error recovery - user exists but tenant is null, status:', onboardingStatus);
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-warm">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                        <AlertTriangle className="h-8 w-8 text-amber-500" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">
                        Error al cargar perfil
                    </h2>
                    <p className="text-gray-600 text-sm mb-6">
                        No se pudo cargar tu perfil de usuario. Esto puede ocurrir por una sesión expirada o un problema de conexión.
                    </p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={retryProfile}
                            className="w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
                        >
                            Reintentar
                        </button>
                        <button
                            onClick={() => signOut()}
                            className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                            Cerrar Sesión
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * POST-LOGIN HANDSHAKE - Operator Route
     *
     * 'operator' (Scenario C): User is fully onboarded - go directly to dashboard
     * This section is ONLY reached when:
     * - User is authenticated (user exists)
     * - Onboarding status is 'operator' (bypassed onboarding check above)
     */
    if (onboardingStatus === 'operator') {
        console.log('[Router] OPERATOR status confirmed - routing to dashboard');
    }

    // Authenticated: load the full app with code splitting
    // UserProvider ensures user.id is never null within AuthenticatedApp
    return (
        <Suspense fallback={<AuthenticatedAppLoader />}>
            <UserProvider>
                <AuthenticatedApp />
            </UserProvider>
        </Suspense>
    );
};

/**
 * SysAdmin Loading Screen
 * Used while lazy loading the SysAdmin module
 */
const SysAdminLoadingScreen: React.FC = () => (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-sm">Cargando Administración de Plataforma...</p>
        </div>
    </div>
);

const App: React.FC = () => {
    // URL-based routing: Check if path starts with /sysadmin
    const isSysAdminPath = typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/sysadmin');

    // Route to SysAdmin Console for /sysadmin/* paths
    if (isSysAdminPath) {
        return (
            <QueryClientProvider client={queryClient}>
                <CalmModeProvider>
                    <AuthProvider>
                        <ToastProvider>
                            <Suspense fallback={<SysAdminLoadingScreen />}>
                                <SysAdminEntry />
                            </Suspense>
                            <ToastContainer />
                        </ToastProvider>
                    </AuthProvider>
                </CalmModeProvider>
            </QueryClientProvider>
        );
    }

    // Standard tenant application for all other paths
    return (
        <QueryClientProvider client={queryClient}>
            <CalmModeProvider>
                <AuthProvider>
                    <TenantProvider>
                        <SystemHealthProvider>
                            <ToastProvider>
                                <AppContent />
                            </ToastProvider>
                        </SystemHealthProvider>
                    </TenantProvider>
                </AuthProvider>
            </CalmModeProvider>
        </QueryClientProvider>
    );
};


export default App;
